import React from 'react'
import { TaskDropdown } from '../TaskDropdown'
import type { TaskEntryData } from '../TaskEntry'
import type { RemovableMarkers } from '../../utils/removableMarkers'
import { TaskMenuTargetContent } from './TaskMenuTargetContent'
import type { TaskMenuHandlers, TaskMenuTarget } from './TaskMenu.types'
import './TaskMenuShortcutBox.scss'

export interface TaskMenuShortcutBoxProps {
  /** Welcher Kasten — derselbe Inhalt wie im Unter-Menü des Aufgaben-Menüs. */
  target: TaskMenuTarget
  task: TaskEntryData
  handlers: TaskMenuHandlers
  /** „Ruhen lassen" im Wann-Kasten; fehlt es, fehlt die Zeile. */
  onRest?: () => void
  /** Was sich entfernen lässt, wenn nicht `task` es sagt — die Pille bei Mehrfachauswahl (`TaskMenuTargetContent`). */
  removable?: RemovableMarkers
  /** Der Kasten hat einen Wert geliefert oder wurde geschlossen. */
  onClose: () => void
  /**
   * Wo der Anker sitzt: `'row'` unten links an der Aufgabenzeile (die Zeile ist sein Bezug),
   * `'below'` als Punkt ohne Maß unter einer Zeile der Checkliste — die Zeilen rücken nicht.
   * `'trigger'`: Der Knopf `trigger` öffnet und schließt ihn, der Kasten geht darüber auf — so an der
   * **Listen-Werkzeug-Pille** (#291).
   */
  anchor: 'row' | 'below' | 'trigger'
  /** Bei `anchor="trigger"`: der Knopf. */
  trigger?: React.ReactElement
  /** Bei `anchor="trigger"` offen/zu; sonst steht der Kasten offen, solange er im Baum ist. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Eine Zeile über dem Inhalt — etwa „Wirkt auf 2 von 3 …“ an der Pille (#291). */
  notice?: string
  /** Titel des Sheets auf schmalen Fenstern; ohne der Titel der Aufgabe. */
  sheetTitle?: string
  /** Ohne Einblenden — beim Wechsel von einem Kasten zum nächsten (Überfahren in der Pille). */
  instant?: boolean
}

/**
 * Der Inhalt mit der Zeile darüber. Eigene Komponente, weil `TaskDropdown` seinem Kind im Sheet
 * `placement` mitgibt — ein Fragment nähme das nicht, ein `div` schriebe es ins DOM.
 */
export const ShortcutBoxBody: React.FC<{ notice?: string; placement?: string; children: React.ReactNode }> = ({ notice, children }) => (
  <>
    {notice && <div className="task-menu-shortcut__notice">{notice}</div>}
    {children}
  </>
)

/**
 * **Der Kasten eines Tastenkürzels** (#205, #213) — W, ⇧W, D, ⇧D, M, T, G an einer angefassten
 * Zeile öffnen ohne Menü denselben Inhalt wie das Unter-Menü, unter der Zeile. Geteilt von der
 * Aufgabenzeile (`TaskEntry`), der Checkliste (`SubtaskMenu`) und der Pille bei Mehrfachauswahl
 * (`SelectionActions`). Klicks darin laufen im React-Baum nicht zur Zeile hoch.
 *
 * Kalender und Tag-Liste lassen unten Platz für die Fußzeile ihres Kastens (`reserveBottom`); auf
 * schmalen Fenstern kommen Wann und Deadline als Sheet mit dem Titel der Aufgabe.
 *
 * @example
 * <TaskMenuShortcutBox target="attention" task={entry} handlers={handlers} onClose={close} anchor="below" />
 * @example
 * <TaskMenuShortcutBox target="start" task={entry} handlers={handlers} onClose={close} anchor="trigger"
 *   trigger={<button>…</button>} open={open} onOpenChange={setOpen} notice="Wirkt auf 2 von 3 …" />
 */
export const TaskMenuShortcutBox: React.FC<TaskMenuShortcutBoxProps> = ({
  target, task, handlers, onRest, removable, onClose, anchor, trigger, open = true, onOpenChange, notice, sheetTitle, instant = false,
}) => {
  const isDate = target === 'start' || target === 'deadline'
  const atTrigger = anchor === 'trigger' && trigger
  const dropdown = (
    <TaskDropdown
      open={open}
      onOpenChange={(next) => {
        if (onOpenChange) onOpenChange(next)
        else if (!next) onClose()
      }}
      side={atTrigger ? 'top' : 'bottom'}
      // Am Knopf mittig über ihm (Sven, 08.10.2026); an der Zeile links an ihrem Anfang.
      align={atTrigger ? 'center' : 'start'}
      // Am Knopf ohne Fläche dahinter: Die Nachbarn in der Pille bleiben erreichbar (#291).
      backdrop={!atTrigger}
      instant={instant}
      reserveBottom={isDate || target === 'tags' ? 52 : 0}
      dateSheet={isDate ? { title: sheetTitle ?? task.title ?? '' } : undefined}
      trigger={atTrigger ? trigger : <span className="task-menu-shortcut__anchor" aria-hidden="true" />}
    >
      <ShortcutBoxBody notice={notice}>
        <TaskMenuTargetContent target={target} task={task} handlers={handlers} onRest={onRest} removable={removable} onClose={onClose} />
      </ShortcutBoxBody>
    </TaskDropdown>
  )
  if (atTrigger) return dropdown
  return (
    <span
      className={`task-menu-shortcut task-menu-shortcut--${anchor}`}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      {dropdown}
    </span>
  )
}
